/**
 * BuilderCompanyPicker — pick which of the owner's building companies (or GC
 * entities) a project is built under, so invited contractors see who they're
 * bidding for. Reusable in the new-bid modal, edit-project, and the preview.
 *
 * - Loads the owner's saved companies (reusable list).
 * - Inline "Add company" (name + optional logo) so a new company can be added
 *   on the spot without leaving for Profile/Settings.
 * - Calls onChange({ id, name, logo }) whenever the selection changes.
 *
 * Logos are stored as base64 data URLs (small, resized client-side).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Plus, Check, ImagePlus, X, Loader2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';

const GREEN = '#5c9e33';

// Downscale an image file to a small square-ish logo and return a base64 data URL.
const fileToLogoBase64 = (file, maxSize = 256) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const BuilderCompanyPicker = ({ userId, value, onChange, label = 'Builder / Company' }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLogo, setNewLogo] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await apiService.listBuilderCompanies(userId);
      setCompanies(res.data || []);
    } catch (e) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const select = (c) => {
    onChange?.(c ? { id: c.id, name: c.name, logo: c.logo || null } : { id: null, name: null, logo: null });
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await fileToLogoBase64(file);
      setNewLogo(b64);
    } catch {
      toast.error('Could not read that image');
    }
  };

  const saveNew = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Enter a company name'); return; }
    setSaving(true);
    try {
      const res = await apiService.createBuilderCompany({ user_id: userId, name, logo: newLogo });
      const created = res.data;
      setCompanies((prev) => [created, ...prev]);
      select(created);
      setAdding(false);
      setNewName('');
      setNewLogo(null);
      toast.success('Company added');
    } catch (e) {
      toast.error('Could not add company');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="builder-company-picker">
      {label && (
        <label className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> {label}
        </label>
      )}

      {!adding ? (
        <div className="mt-1.5 space-y-2">
          <div className="flex flex-wrap gap-2">
            {loading && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>}
            {!loading && companies.map((c) => {
              const active = value === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => select(active ? null : c)}
                  data-testid={`builder-company-option-${c.id}`}
                  className={`inline-flex items-center gap-2 rounded-xl border-2 px-3 py-2 transition ${active ? 'bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  style={active ? { borderColor: GREEN } : {}}
                >
                  {c.logo ? (
                    <img src={c.logo} alt="" className="w-6 h-6 rounded object-contain bg-white" />
                  ) : (
                    <span className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    </span>
                  )}
                  <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                  {active && <Check className="w-4 h-4" style={{ color: GREEN }} />}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="builder-company-add-btn"
              className="inline-flex items-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-500 hover:border-emerald-400 hover:text-emerald-600 transition"
            >
              <Plus className="w-4 h-4" /> Add company
            </button>
          </div>
          {!loading && companies.length === 0 && (
            <p className="text-xs text-slate-400">No companies yet — add the one this project is built under.</p>
          )}
        </div>
      ) : (
        <div className="mt-1.5 rounded-xl border-2 border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="builder-company-add-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-700">New company</p>
            <button type="button" onClick={() => { setAdding(false); setNewLogo(null); setNewName(''); }} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="builder-company-logo-btn"
              className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-300 bg-white flex items-center justify-center overflow-hidden hover:border-emerald-400 flex-shrink-0"
            >
              {newLogo ? <img src={newLogo} alt="" className="w-full h-full object-contain" /> : <ImagePlus className="w-5 h-5 text-slate-400" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoPick} data-testid="builder-company-logo-input" />
            <div className="flex-1">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Summit Homes LLC"
                data-testid="builder-company-name-input"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <p className="text-[11px] text-slate-400 mt-1">Logo optional — used for branding on bid requests.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setAdding(false); setNewLogo(null); setNewName(''); }} className="text-sm font-semibold text-slate-500 px-3 py-2">Cancel</button>
            <button
              type="button"
              onClick={saveNew}
              disabled={saving}
              data-testid="builder-company-save-btn"
              className="inline-flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-60"
              style={{ background: GREEN }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save & select
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderCompanyPicker;
